import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import {
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeftRight,
  Plus,
  Trash2,
  Calendar,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  DollarSign,
} from 'lucide-react';
import useStore from '../../store/useStore';
import { Card, KPICard } from '../../components/ui/Card';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/ui/PageHeader';
import { formatCurrency, formatDate, formatShortDate } from '../../utils/format';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CATEGORIES = ['Salary', 'CC Payment', 'Rent', 'Car', 'Venmo', 'Reimbursement', 'Tax', 'Tax Refund', 'Cash', 'Travel', 'Side Income', 'Other Income', 'Other'];

// ─── Chart Tooltip ──────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-text-secondary text-xs mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} className="text-sm" style={{ color: entry.color || entry.fill }}>
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
}

// ─── Account Picker (web) ───────────────────────────────────────────────────
function AccountPicker({ selectedId, onSelect, label }) {
  const accounts = useStore((s) => s.accounts);
  const creditCards = useStore((s) => s.creditCards);

  const options = useMemo(() => [
    { id: '', label: 'None' },
    ...accounts.map((a) => ({ id: a.id, label: a.name })),
    ...creditCards.map((c) => ({ id: c.id, label: c.name + (c.lastFour ? ` ${c.lastFour}` : '') })),
  ], [accounts, creditCards]);

  const inputClass =
    'bg-bg-tertiary border border-border/50 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent w-full';

  return (
    <div className="w-[140px]">
      <label className="text-[10px] text-text-muted uppercase tracking-wider mb-1 block">{label}</label>
      <select value={selectedId || ''} onChange={(e) => onSelect(e.target.value || null)} className={inputClass}>
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ─── Transaction Add Form ───────────────────────────────────────────────────
function TransactionForm({ onSubmit }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    category: 'Other',
    type: 'expense',
    addToAccountId: null,
    subtractFromAccountId: null,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) return;
    onSubmit({ ...form, amount: Number(form.amount) });
    setForm((prev) => ({ ...prev, description: '', amount: '', addToAccountId: null, subtractFromAccountId: null }));
  };

  const inputClass =
    'bg-bg-tertiary border border-border/50 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent w-full';

  return (
    <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
      <div className="w-[130px]">
        <label className="text-[10px] text-text-muted uppercase tracking-wider mb-1 block">Date</label>
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputClass} />
      </div>
      <div className="flex-[2] min-w-[140px]">
        <label className="text-[10px] text-text-muted uppercase tracking-wider mb-1 block">Description</label>
        <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Paycheck" className={inputClass} />
      </div>
      <div className="w-[100px]">
        <label className="text-[10px] text-text-muted uppercase tracking-wider mb-1 block">Amount</label>
        <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" className={inputClass} />
      </div>
      <div className="w-[100px]">
        <label className="text-[10px] text-text-muted uppercase tracking-wider mb-1 block">Type</label>
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputClass}>
          <option value="expense">Expense</option>
          <option value="income">Income</option>
        </select>
      </div>
      <div className="w-[120px]">
        <label className="text-[10px] text-text-muted uppercase tracking-wider mb-1 block">Category</label>
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputClass}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <AccountPicker label="Add To" selectedId={form.addToAccountId} onSelect={(id) => setForm({ ...form, addToAccountId: id })} />
      <AccountPicker label="Subtract From" selectedId={form.subtractFromAccountId} onSelect={(id) => setForm({ ...form, subtractFromAccountId: id })} />
      <button type="submit" className="flex items-center gap-1 px-4 py-2 text-xs bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors">
        <Plus className="w-3.5 h-3.5" /> Add
      </button>
    </form>
  );
}

// ─── Cell Tooltip (hover to see underlying transactions) ────────────────────
function CellTooltip({ transactions: txs, total, children, className = '', align = 'right' }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState(null);
  const cellRef = useRef(null);
  const sorted = useMemo(() => [...txs].sort((a, b) => a.date.localeCompare(b.date)), [txs]);

  const handleEnter = useCallback(() => {
    if (cellRef.current) {
      const rect = cellRef.current.getBoundingClientRect();
      setPos({ top: rect.top, right: window.innerWidth - rect.right, left: rect.left, width: rect.width });
    }
    setShow(true);
  }, []);

  return (
    <Td
      ref={cellRef}
      align={align}
      className={className}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
    >
      <span className="cursor-default border-b border-dotted border-current/30">{children}</span>
      {show && sorted.length > 0 && pos && createPortal(
        <div
          className="fixed z-[9999] w-80 bg-bg-card border border-border rounded-lg shadow-lg p-3 pointer-events-none"
          style={{ top: pos.top - 8, right: pos.right, transform: 'translateY(-100%)' }}
        >
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {sorted.map((tx, i) => (
              <div key={tx.id || i} className="flex items-center justify-between gap-2 text-[11px]">
                <span className="text-text-muted font-mono shrink-0 w-16">{formatShortDate(tx.date)}</span>
                <span className="text-text-secondary truncate flex-1">{tx.description || tx.category}</span>
                <span className={`font-mono shrink-0 ${tx.type === 'income' ? 'text-success' : 'text-danger'}`}>
                  {tx.type === 'expense' ? '-' : ''}{formatCurrency(tx.amount)}
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-border/40 mt-1.5 pt-1.5 flex justify-between text-xs font-semibold">
            <span className="text-text-muted">{sorted.length} transaction{sorted.length !== 1 ? 's' : ''}</span>
            <span className="font-mono text-text-primary">{formatCurrency(total)}</span>
          </div>
        </div>,
        document.body
      )}
    </Td>
  );
}

// ─── Monthly Dashboard (computed from transactions) ─────────────────────────
const CC_COMPUTE_CUTOFF = '2026-03'; // Auto-compute CC Spend from this month onward

// Helper: get previous month key from YYYY-MM
function prevMonthKey(key) {
  const [y, m] = key.split('-').map(Number);
  const pm = m === 1 ? 12 : m - 1;
  const py = m === 1 ? y - 1 : y;
  return `${py}-${String(pm).padStart(2, '0')}`;
}

function MonthlyDashboard({ transactions, budget, cashFlowMonthly, creditCards, balanceSheetMonthly }) {
  const addCashFlowMonthly = useStore((s) => s.addCashFlowMonthly);
  const updateCashFlowMonthly = useStore((s) => s.updateCashFlowMonthly);
  const frozenRef = useRef(false);

  // Build a lookup for CC spend from cash_flow_monthly (Supabase data only)
  // Keyed by YYYY-MM, also stores the row id for updates.
  const ccSpendMap = useMemo(() => {
    const map = {};
    (cashFlowMonthly || []).forEach((m) => {
      const parts = (m.month || '').split(' ');
      if (parts.length === 2) {
        const mi = MONTH_NAMES.indexOf(parts[0]);
        if (mi >= 0) {
          const key = `${parts[1]}-${String(mi + 1).padStart(2, '0')}`;
          map[key] = { ccSpend: m.ccSpend || 0, id: m.id };
        }
      }
    });
    return map;
  }, [cashFlowMonthly]);

  // Build CC ending balance map from balance_sheet_monthly snapshots
  const ccBalanceMap = useMemo(() => {
    const map = {};
    (balanceSheetMonthly || []).forEach((row) => {
      if (row.month) map[row.month] = row.creditCards || 0;
    });
    return map;
  }, [balanceSheetMonthly]);

  // Current live CC balance (sum of all card balances)
  const liveCCBalance = useMemo(() => {
    return (creditCards || []).reduce((sum, c) => sum + Math.abs(c.balance || 0), 0);
  }, [creditCards]);

  // Freeze CC Spend for completed months >= 2026-03 that don't have a frozen value yet
  useEffect(() => {
    if (frozenRef.current) return;
    frozenRef.current = true;

    const now = new Date();
    const curMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    // Group transactions by month for CC Payment totals
    const ccPaymentsByMonth = {};
    transactions.forEach((tx) => {
      if (tx.type !== 'expense' || tx.category !== 'CC Payment') return;
      const key = typeof tx.date === 'string' ? tx.date.substring(0, 7) : `${new Date(tx.date).getFullYear()}-${String(new Date(tx.date).getMonth() + 1).padStart(2, '0')}`;
      ccPaymentsByMonth[key] = (ccPaymentsByMonth[key] || 0) + tx.amount;
    });

    // Find completed months >= cutoff that need freezing
    const monthsWithTxs = new Set();
    transactions.forEach((tx) => {
      const key = typeof tx.date === 'string' ? tx.date.substring(0, 7) : `${new Date(tx.date).getFullYear()}-${String(new Date(tx.date).getMonth() + 1).padStart(2, '0')}`;
      monthsWithTxs.add(key);
    });

    [...monthsWithTxs]
      .filter((key) => key >= CC_COMPUTE_CUTOFF && key < curMonthKey)
      .forEach((key) => {
        const existing = ccSpendMap[key];
        // Skip if already has a frozen CC spend value
        if (existing && existing.ccSpend > 0) return;

        // Compute: Ending Balance - Previous Month Balance + CC Payments
        const endingBal = ccBalanceMap[key] || 0;
        const prevBal = ccBalanceMap[prevMonthKey(key)] || 0;
        const ccPayments = ccPaymentsByMonth[key] || 0;
        const ccSpend = Math.round(Math.max(0, endingBal - prevBal + ccPayments) * 100) / 100;

        if (ccSpend <= 0) return;

        // Build month label (e.g. "Mar 2026")
        const [y, m] = key.split('-').map(Number);
        const monthLabel = `${MONTH_NAMES[m - 1]} ${y}`;

        if (existing) {
          // Update existing row with computed CC spend
          updateCashFlowMonthly(existing.id, { ccSpend });
        } else {
          // Create new row to freeze the CC spend
          addCashFlowMonthly({ month: monthLabel, year: y, ccSpend });
        }
      });
  }, [transactions, ccSpendMap, ccBalanceMap, addCashFlowMonthly, updateCashFlowMonthly]);

  const monthlyData = useMemo(() => {
    // Group transactions by YYYY-MM
    const byMonth = {};
    transactions.forEach((tx) => {
      // Extract YYYY-MM directly from date string to avoid UTC timezone shift
      const key = typeof tx.date === 'string' ? tx.date.substring(0, 7) : `${new Date(tx.date).getFullYear()}-${String(new Date(tx.date).getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = { inflows: [], outflows: [] };
      if (tx.type === 'income') byMonth[key].inflows.push(tx);
      else byMonth[key].outflows.push(tx);
    });

    const now = new Date();
    const curMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    // Build sorted monthly rows
    return Object.keys(byMonth)
      .sort()
      .map((key) => {
        const [y, m] = key.split('-').map(Number);
        const { inflows, outflows } = byMonth[key];
        const cashInflow = inflows.reduce((s, t) => s + t.amount, 0);
        const cashOutflow = outflows.reduce((s, t) => s + t.amount, 0);
        const ccPayments = outflows.filter((t) => t.category === 'CC Payment').reduce((s, t) => s + t.amount, 0);
        const rent = outflows.filter((t) => t.category === 'Rent').reduce((s, t) => s + t.amount, 0);
        const car = outflows.filter((t) => t.category === 'Car').reduce((s, t) => s + t.amount, 0);
        const netCashflow = cashInflow - cashOutflow;
        const nonRentCF = cashInflow - (cashOutflow - rent - car);

        // CC Spend logic:
        // - Pre-2026-03: use seeded/frozen values from cash_flow_monthly
        // - Current month (>= 2026-03): compute dynamically from live CC balance
        // - Past months (>= 2026-03): use frozen value from cash_flow_monthly (persisted on month close)
        let ccSpend = 0;
        const frozen = ccSpendMap[key];
        if (key >= CC_COMPUTE_CUTOFF && key === curMonthKey) {
          // Current month — compute dynamically
          const prevBal = ccBalanceMap[prevMonthKey(key)] || 0;
          ccSpend = Math.max(0, liveCCBalance - prevBal + ccPayments);
        } else if (frozen) {
          // Historical or already-frozen month
          ccSpend = frozen.ccSpend;
        }

        return {
          key,
          label: `${MONTH_NAMES[m - 1]} ${y}`,
          shortLabel: `${MONTH_NAMES[m - 1]} '${String(y).slice(2)}`,
          year: y,
          month: m,
          cashInflow,
          cashOutflow,
          ccPayments,
          ccSpend,
          rent,
          car,
          netCashflow,
          nonRentCF,
          inflows,
          outflows,
        };
      });
  }, [transactions, ccSpendMap, ccBalanceMap, liveCCBalance]);

  // Current month for highlighting
  const now = new Date();
  const curKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  return (
    <div>
      {/* Net Cashflow Chart */}
      <Card className="section-gap">
        <h3 className="text-sm font-semibold text-text-primary mb-4">Monthly Net Cash Flow</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#30363D" />
              <XAxis
                dataKey="shortLabel"
                tick={{ fill: '#8B949E', fontSize: 10 }}
                axisLine={{ stroke: '#30363D' }}
                tickLine={false}
                angle={-35}
                textAnchor="end"
                height={55}
              />
              <YAxis
                tick={{ fill: '#8B949E', fontSize: 11 }}
                axisLine={{ stroke: '#30363D' }}
                tickLine={false}
                tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`}
              />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine y={0} stroke="#30363D" />
              <Bar dataKey="netCashflow" name="Net Cash Flow" radius={[3, 3, 0, 0]}>
                {monthlyData.map((entry, i) => (
                  <Cell key={i} fill={entry.netCashflow >= 0 ? '#10B981' : '#EF4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Monthly Detail Table */}
      <Card>
        <h3 className="text-sm font-semibold text-text-primary mb-4">Monthly Summary</h3>
        <div className="overflow-x-auto">
          <Table>
            <Thead>
              <tr>
                <Th>Month</Th>
                <Th align="right">Cash Inflow</Th>
                <Th align="right">Cash Outflow</Th>
                <Th align="right">CC Spend</Th>
                <Th align="right">Net CF</Th>
                <Th align="right">Non-Rent CF</Th>
                {budget > 0 && <Th align="right">Budget</Th>}
              </tr>
            </Thead>
            <tbody>
              {[...monthlyData].reverse().map((m) => {
                const isCurrent = m.key === curKey;
                return (
                  <Tr key={m.key} className={isCurrent ? 'bg-accent/5' : ''}>
                    <Td className={`text-text-primary font-medium ${isCurrent ? 'text-accent' : ''}`}>
                      {m.label}
                    </Td>
                    <CellTooltip transactions={m.inflows} total={m.cashInflow} className="font-mono text-success tabular-nums">
                      {formatCurrency(m.cashInflow)}
                    </CellTooltip>
                    <CellTooltip transactions={m.outflows} total={m.cashOutflow} className="font-mono text-danger tabular-nums">
                      {formatCurrency(m.cashOutflow)}
                    </CellTooltip>
                    <Td align="right" className="font-mono text-text-secondary tabular-nums">
                      {m.ccSpend > 0 ? formatCurrency(m.ccSpend) : '—'}
                    </Td>
                    <CellTooltip transactions={[...m.inflows, ...m.outflows]} total={m.netCashflow} className={`font-mono font-semibold tabular-nums ${m.netCashflow >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatCurrency(m.netCashflow)}
                    </CellTooltip>
                    <CellTooltip transactions={[...m.inflows, ...m.outflows.filter((t) => t.category !== 'Rent' && t.category !== 'Car')]} total={m.nonRentCF} className={`font-mono tabular-nums ${m.nonRentCF >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatCurrency(m.nonRentCF)}
                    </CellTooltip>
                    {budget > 0 && (
                      <Td align="right" className="font-mono text-text-muted tabular-nums">
                        {formatCurrency(budget)}
                      </Td>
                    )}
                  </Tr>
                );
              })}
              {monthlyData.length === 0 && (
                <Tr>
                  <Td colSpan={7} className="text-center text-text-muted py-8">
                    No transaction data yet.
                  </Td>
                </Tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

// ─── Inline Editable Cell ───────────────────────────────────────────────────
function EditableCell({ value, onSave, type = 'text', className = '', selectOptions }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef(null);

  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const parsed = type === 'number' ? parseFloat(draft) || 0 : draft;
    if (parsed !== value) onSave(parsed);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') { setDraft(value); setEditing(false); }
  };

  if (editing) {
    const inputClass = 'bg-bg-tertiary border border-accent/60 rounded px-1.5 py-0.5 text-xs text-text-primary focus:outline-none w-full';
    if (selectOptions) {
      return (
        <select ref={ref} value={draft} onChange={(e) => { setDraft(e.target.value); }} onBlur={commit} onKeyDown={handleKey} className={inputClass}>
          {selectOptions.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    return (
      <input
        ref={ref}
        type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
        step={type === 'number' ? '0.01' : undefined}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        className={`${inputClass} ${type === 'number' ? 'text-right font-mono [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' : ''}`}
      />
    );
  }

  return (
    <span onClick={() => { setDraft(value); setEditing(true); }} className={`cursor-pointer hover:bg-bg-tertiary/60 rounded px-1 -mx-1 transition-colors ${className}`}>
      {type === 'number' ? formatCurrency(value) : type === 'date' ? formatDate(value) : (value || '—')}
    </span>
  );
}

// ─── Inline Account Cell Editor ─────────────────────────────────────────────
function AccountCellEditor({ accountId, onSave }) {
  const accounts = useStore((s) => s.accounts);
  const creditCards = useStore((s) => s.creditCards);
  const [editing, setEditing] = useState(false);
  const ref = useRef(null);

  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const allAccounts = useMemo(() => [
    ...accounts.map((a) => ({ id: a.id, label: a.name })),
    ...creditCards.map((c) => ({ id: c.id, label: c.name + (c.lastFour ? ` ${c.lastFour}` : '') })),
  ], [accounts, creditCards]);

  const displayName = useMemo(() => {
    if (!accountId) return '—';
    const found = allAccounts.find((a) => a.id === accountId);
    return found?.label || '—';
  }, [accountId, allAccounts]);

  if (editing) {
    return (
      <select
        ref={ref}
        value={accountId || ''}
        onChange={(e) => { onSave(e.target.value || null); setEditing(false); }}
        onBlur={() => setEditing(false)}
        className="bg-bg-tertiary border border-accent/60 rounded px-1.5 py-0.5 text-xs text-text-primary focus:outline-none w-full"
      >
        <option value="">None</option>
        {allAccounts.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
      </select>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className="cursor-pointer hover:bg-bg-tertiary/60 rounded px-1 -mx-1 transition-colors text-text-muted"
    >
      {displayName}
    </span>
  );
}

// ─── Transaction List View ──────────────────────────────────────────────────
function TransactionList({ transactions, onAdd, onUpdate, onRemove }) {
  const [page, setPage] = useState(0);
  const [filterCat, setFilterCat] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const PAGE_SIZE = 50;

  const filtered = useMemo(() => {
    let txs = [...transactions].sort((a, b) => new Date(b.date) - new Date(a.date));
    if (filterCat !== 'All') txs = txs.filter((t) => t.category === filterCat);
    if (filterType !== 'All') txs = txs.filter((t) => t.type === filterType);
    return txs;
  }, [transactions, filterCat, filterType]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageData = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const selectClass =
    'bg-bg-tertiary border border-border/50 rounded-lg px-2 py-1.5 text-xs text-text-primary focus:outline-none focus:border-accent';

  return (
    <div className="space-y-4">
      <Card>
        <h3 className="text-sm font-semibold text-text-primary mb-3">Add Transaction</h3>
        <TransactionForm onSubmit={onAdd} />
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-text-primary">
            Transactions ({filtered.length})
          </h3>
          <div className="flex items-center gap-2">
            <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(0); }} className={selectClass}>
              <option value="All">All Types</option>
              <option value="income">Income</option>
              <option value="expense">Expense</option>
            </select>
            <select value={filterCat} onChange={(e) => { setFilterCat(e.target.value); setPage(0); }} className={selectClass}>
              <option value="All">All Categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table className="min-w-[900px]">
            <Thead>
              <tr>
                <Th className="w-[110px]">Date</Th>
                <Th>Description</Th>
                <Th className="w-[110px]">Type</Th>
                <Th className="w-[130px]">Category</Th>
                <Th align="right" className="w-[110px]">Amount</Th>
                <Th className="w-[120px]">Add To</Th>
                <Th className="w-[120px]">Subtract From</Th>
                <Th align="right" className="w-8" />
              </tr>
            </Thead>
            <tbody>
              {pageData.map((tx) => (
                <Tr key={tx.id}>
                  <Td className="text-text-secondary text-xs whitespace-nowrap">
                    <EditableCell value={tx.date} type="date" onSave={(v) => onUpdate(tx.id, { date: v })} />
                  </Td>
                  <Td className="text-text-primary text-xs">
                    <EditableCell value={tx.description} onSave={(v) => onUpdate(tx.id, { description: v })} />
                  </Td>
                  <Td>
                    <EditableCell
                      value={tx.type}
                      selectOptions={['income', 'expense']}
                      onSave={(v) => onUpdate(tx.id, { type: v })}
                      className={`text-xs font-medium ${tx.type === 'income' ? 'text-success' : 'text-danger'}`}
                    />
                  </Td>
                  <Td>
                    <EditableCell
                      value={tx.category || 'Other'}
                      selectOptions={CATEGORIES}
                      onSave={(v) => onUpdate(tx.id, { category: v })}
                      className="text-xs"
                    />
                  </Td>
                  <Td align="right">
                    <EditableCell
                      value={tx.amount}
                      type="number"
                      onSave={(v) => onUpdate(tx.id, { amount: v })}
                      className={`font-mono font-semibold tabular-nums text-xs ${tx.type === 'income' ? 'text-success' : 'text-danger'}`}
                    />
                  </Td>
                  <Td className="text-xs">
                    <AccountCellEditor
                      accountId={tx.addToAccountId}
                      onSave={(id) => onUpdate(tx.id, { addToAccountId: id })}
                    />
                  </Td>
                  <Td className="text-xs">
                    <AccountCellEditor
                      accountId={tx.subtractFromAccountId}
                      onSave={(id) => onUpdate(tx.id, { subtractFromAccountId: id })}
                    />
                  </Td>
                  <Td align="right">
                    <button onClick={() => onRemove(tx.id)} className="text-text-muted hover:text-danger transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </Td>
                </Tr>
              ))}
              {pageData.length === 0 && (
                <Tr>
                  <Td colSpan={8} className="text-center text-text-muted py-8">
                    No transactions match the filters.
                  </Td>
                </Tr>
              )}
            </tbody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/30">
            <span className="text-xs text-text-muted">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Pay Schedule Helpers (mirrored from PaycheckSnapshot) ──────────────────

function lastDayOfMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function adjustForWeekend(date) {
  const d = new Date(date);
  const day = d.getDay();
  if (day === 6) d.setDate(d.getDate() - 1); // Sat → Fri
  if (day === 0) d.setDate(d.getDate() - 2); // Sun → Fri
  return d;
}

/** Generate semi-monthly pay dates (15th and EOM, adjusted for weekends) up to today */
function generateAllPayDates(year) {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const dates = [];
  for (let month = 0; month < 12; month++) {
    const mid = adjustForWeekend(new Date(year, month, 15));
    if (mid <= today) dates.push(mid);
    const eom = adjustForWeekend(new Date(year, month, lastDayOfMonth(year, month)));
    if (eom <= today) dates.push(eom);
  }
  return dates;
}

/** Generate all 24 semi-monthly pay dates for a full year (15th and EOM, adjusted for weekends) */
function generateFullYearPayDates(year) {
  const dates = [];
  for (let month = 0; month < 12; month++) {
    dates.push(adjustForWeekend(new Date(year, month, 15)));
    dates.push(adjustForWeekend(new Date(year, month, lastDayOfMonth(year, month))));
  }
  return dates;
}

const DEFAULT_PAYCHECK = {
  grossPay: 0,
  federalTax: 0,
  stateTax: 0,
  socialSecurity: 0,
  medicare: 0,
  retirement401k: 0,
  hsa: 0,
  healthInsurance: 0,
  otherDeductions: 0,
  netPay: 0,
  employer: '',
};

// ─── Main Component ─────────────────────────────────────────────────────────
export default function CashFlow() {
  const [view, setView] = useState('monthly');
  const transactions = useStore((s) => s.transactions);
  const cashFlowMonthly = useStore((s) => s.cashFlowMonthly);
  const creditCards = useStore((s) => s.creditCards);
  const balanceSheetMonthly = useStore((s) => s.balanceSheetMonthly);
  const addTransaction = useStore((s) => s.addTransaction);
  const updateTransaction = useStore((s) => s.updateTransaction);
  const removeTransaction = useStore((s) => s.removeTransaction);
  const budgetConfig = useStore((s) => s.userSettings?.budgetConfig);
  const autoPostSalary = useStore((s) => s.userSettings?.autoPostSalary !== false); // default true
  const updateUserSettings = useStore((s) => s.updateUserSettings);
  const paycheckSnapshots = useStore((s) => s.paycheckSnapshots);
  const autoPosted = useRef(false);

  // Get baseline paycheck (most recent snapshot, or DEFAULT_PAYCHECK fallback)
  const baseline = useMemo(() => {
    if (!paycheckSnapshots?.length) return DEFAULT_PAYCHECK;
    const sorted = [...paycheckSnapshots].sort((a, b) => new Date(b.payDate) - new Date(a.payDate));
    return sorted[0];
  }, [paycheckSnapshots]);

  // Auto-posting of salary transactions is handled by useAutoPostings hook
  // (removed duplicate auto-posting logic that was recreating deleted transactions)

  // Compute budget Est. Cash Outflow from paycheck baseline
  const budgetEstCash = useMemo(() => {
    if (!baseline || !budgetConfig) return 0;

    const grossRevenue = (baseline.grossPay || 0) * 2;
    const taxes = ((baseline.federalTax || 0) + (baseline.stateTax || 0) + (baseline.socialSecurity || 0) + (baseline.medicare || 0)) * 2;
    const benefits = ((baseline.healthInsurance || 0) + (baseline.otherDeductions || 0) + (baseline.retirement401k || 0) + (baseline.hsa || 0)) * 2;
    const netRevenue = grossRevenue - taxes - benefits;
    const { rothIra = 0, roth401k = 0, hsa = 0, lifeInsurance = 0, rent = 0, carPayment = 0, brokeragePct = 50 } = budgetConfig;
    const totalAlloc = rothIra + roth401k + hsa + lifeInsurance + rent + carPayment;
    const unallocated = netRevenue - totalAlloc;
    const pct = Math.max(0, Math.min(100, brokeragePct));
    const brokerage = Math.round(unallocated * (pct / 100) * 100) / 100;
    return Math.round((unallocated - brokerage) * 100) / 100;
  }, [baseline, budgetConfig]);

  // KPIs for current month
  const kpis = useMemo(() => {
    const now = new Date();
    const curKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const curTxs = transactions.filter((t) => {
      const key = typeof t.date === 'string' ? t.date.substring(0, 7) : `${new Date(t.date).getFullYear()}-${String(new Date(t.date).getMonth() + 1).padStart(2, '0')}`;
      return key === curKey;
    });
    const inflow = curTxs.filter((t) => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const outflow = curTxs.filter((t) => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    const net = inflow - outflow;
    return { inflow, outflow, net, count: transactions.length };
  }, [transactions]);

  const tabClass = (active) =>
    `flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-all duration-150 ${
      active
        ? 'text-accent bg-accent/10 font-medium'
        : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary/40'
    }`;

  return (
    <div>
      <PageHeader title="Cash Flow" subtitle="Bank-level cash inflows & outflows" />

      {/* KPI Cards — Current Month */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 kpi-grid">
        <KPICard title="Inflow (This Mo)" value={formatCurrency(kpis.inflow)} icon={ArrowUpRight} />
        <KPICard title="Outflow (This Mo)" value={formatCurrency(kpis.outflow)} icon={ArrowDownRight} variant="inverse" />
        <KPICard title="Net CF (This Mo)" value={formatCurrency(kpis.net)} icon={ArrowLeftRight} />
        <KPICard title="Total Transactions" value={kpis.count.toString()} icon={BarChart3} />
      </div>

      {/* View Toggle */}
      <div className="flex items-center gap-1 mb-3">
        <button onClick={() => setView('monthly')} className={tabClass(view === 'monthly')}>
          <BarChart3 className="w-3.5 h-3.5" /> Monthly
        </button>
        <button onClick={() => setView('transactions')} className={tabClass(view === 'transactions')}>
          <Calendar className="w-3.5 h-3.5" /> Transactions
        </button>
      </div>

      {view === 'monthly' && (
        <MonthlyDashboard transactions={transactions} budget={budgetEstCash} cashFlowMonthly={cashFlowMonthly} creditCards={creditCards} balanceSheetMonthly={balanceSheetMonthly} />
      )}

      {view === 'transactions' && (
        <>
          <TransactionList transactions={transactions} onAdd={addTransaction} onUpdate={updateTransaction} onRemove={removeTransaction} />
        </>
      )}
    </div>
  );
}
